import { createAndUpdateTask, createOpportunity } from "@repo/api";
import { Button } from "@repo/skapa";
import { t } from "@repo/translation";
import { useToast } from "@repo/ui/src/components/Toast";
import { useSettings } from "@repo/ui/src/settings/SettingsProvider";
import { isConsentValid } from "@repo/ui/src/utils/consentHelpers";
import {
  getErrorMessage,
  hasItems,
  isDefined,
  trackAnalytics,
} from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { useState } from "react";

import { type NewProjectState } from "../NewProjectReducer";
import { getProjectPayload } from "../getProjectPayload";

interface Props {
  state: NewProjectState;
  closeModal: () => void;
  onProjectCreated: (projectId: number) => void;
}

export const CreateProjectButton = ({
  state,
  closeModal,
  onProjectCreated,
}: Props) => {
  const { market, language, consent_requirements } = useSettings();

  const { updateToast } = useToast();
  const [loading, setLoading] = useState<boolean>(false);

  const isValid =
    !isEmpty(state.opportunityInfo?.title) &&
    hasItems(state.opportunityInfo.hfb) &&
    (isDefined(state.assignedCoworker) || isDefined(state.assignedTeam)) &&
    isConsentValid(
      state.customerConsents,
      state.manualConsents,
      consent_requirements?.disable_project_creation
    );

  return (
    <Button
      type="primary"
      text={t("action.create")}
      loading={loading}
      onClick={() => {
        setLoading(true);
        createOpportunity(getProjectPayload(state, market, language))
          .then(async (result) => {
            setLoading(false);
            updateToast({
              title: t("prompt.new_opportunity_created"),
            });

            if (state.associatedTask) {
              await createAndUpdateTask({
                ...state.associatedTask,
                opportunityId: result.id,
              });
            }

            closeModal();
            onProjectCreated(result.id);
          })
          .catch((err) => {
            updateToast({
              title: t("prompt.new_opportunity_failed"),
              message: getErrorMessage(err),
              type: "error",
            });
            trackAnalytics("new_opportunity_failed");
          });
      }}
      disabled={!isValid}
    />
  );
};
